import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  parametrization,
  renewalSettings,
  type RenewalModality,
  type RenewalModalityValues,
  type RenewalNotifiedRole,
  type RenewalSettings,
  type RenewalVersion,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { LinkComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { IconChip } from '@/shared/ui/IconChip';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { PageHeader } from '@/shared/ui/PageHeader';
import { RichTextEditor, type RichTextEditorHandle } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import {
  AtivarVersao,
  HistoricoDoParametro,
  InserirTag,
  SemRotina,
  SituacaoDaModalidade,
  SituacaoDaVersao,
  VersoesDoParametro,
  Voltar,
} from './AnniversaryRenewalShared';
import {
  diasDoCampo,
  nomeDoIndice,
  textoDosDias,
  useIndices,
} from './anniversaryRenewal';
import { tomDaParametrizacao } from './tonsDaParametrizacao';
import { Trilha } from './Trilha';

/**
 * Renovacao de apolices na parametrizacao (P-PAR-3): as telas `/parametrizacao/renovacao`,
 * a configuracao da renovacao automatica (aviso, perfis avisados e e-mail) e os
 * parametros por modalidade, como no OnPoint. Tudo e parametro versionado; nenhuma
 * cotacao de renovacao e criada, nenhuma apolice e emitida ou cancelada e nenhum aviso
 * e enviado ate a homologacao (REN-001, REN-002, LIM-010, EMI-002, PJ-01).
 */

const PERM_VER = 'parametrizacao.renovacao.view';
const PERM_EDITAR = 'parametrizacao.renovacao.update';
const PERM_ATIVAR = 'parametrizacao.renovacao.approve';

const BASE = '/parametrizacao/renovacao';
const CONFIGURACAO = `${BASE}/configuracao`;
const MODALIDADES = `${BASE}/modalidades`;

const trilhaDaRenovacao = [
  { rotulo: 'Parametrizacao', para: '/parametrizacao' },
  { rotulo: 'Renovacao de Apolices', para: BASE },
];

const regrasPendentes = [
  'REN-001',
  'REN-002',
  'LIM-010',
  'EMI-002',
  'F-14',
  'F-18',
  'PEN-R2-07',
  'PJ-01',
];

function SemAcesso({ cabecalho }: { cabecalho: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      {cabecalho}
      <Alert tone="info" title="Sem acesso">
        O seu acesso nao inclui os parametros da renovacao ({PERM_VER}).
      </Alert>
    </div>
  );
}

function situacaoDoParametro(dados: RenewalSettings | undefined): string {
  if (!dados) return '';
  const partes = [
    dados.active_setting
      ? `Em vigor: versao ${dados.active_setting.version}`
      : 'Sem versao em vigor',
  ];
  if (dados.draft_setting)
    partes.push(`rascunho ${dados.draft_setting.version} aberto`);
  return partes.join('; ');
}

// --- /parametrizacao/renovacao -------------------------------------------------------

/**
 * Tela "Renovacao" da parametrizacao (PAR-173 a PAR-176): a secao "Cadastrados" com o
 * cartao "Renovacao Automatica de Apolices" e o "Historico de alteracoes".
 */
export function RenewalParametersPage() {
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const consulta = useQuery({
    queryKey: ['renewal-settings'],
    queryFn: ({ signal }) => renewalSettings.get(signal),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha
        itens={[{ rotulo: 'Parametrizacao', para: '/parametrizacao' }]}
        atual="Renovacao de Apolices"
      />
      <PageHeader
        title="Renovacao de Apolices"
        description="Parametros da renovacao automatica: aviso, perfis avisados e prazos por modalidade."
        actions={
          podeLer ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setVendoHistorico(true)}
            >
              Historico de alteracoes
            </Button>
          ) : undefined
        }
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      <SemRotina regras={regrasPendentes}>
        Os parametros ficam gravados com versao. Nenhuma renovacao automatica, emissao
        automatica ou cancelamento automatico roda, e nenhum aviso e enviado. O prazo
        para criacao da cotacao em vigor de cada modalidade define a janela da fila de
        Renovacoes.
      </SemRotina>
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a renovacao">
          {consulta.error.message}
        </Alert>
      ) : null}
      <section className="flex flex-col gap-3" aria-label="Cadastrados">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          Cadastrados
        </h3>
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <li className="surface-card flex h-full flex-col gap-3 p-5 transition-shadow duration-(--duration-base) hover:shadow-(--shadow-md)">
            {/* O chip tem a cor do assunto no menu da parametrizacao. */}
            <div className="flex items-center gap-3">
              <IconChip icon="refresh" tone={tomDaParametrizacao.renovacao} />
              <Link
                to={CONFIGURACAO}
                className="text-sm font-semibold text-ink-900 hover:text-brand-700 hover:underline"
              >
                Renovacao Automatica de Apolices
              </Link>
            </div>
            <p className="text-xs text-ink-600">
              Aviso, perfis de usuario para e-mail e o e-mail de aviso.
            </p>
            {consulta.isPending ? (
              <Skeleton className="h-4 w-40" />
            ) : (
              <Badge
                tone={consulta.data?.active_setting ? 'success' : 'neutral'}
                dot
                className="self-start"
              >
                {situacaoDoParametro(consulta.data)}
              </Badge>
            )}
            <Link
              to={MODALIDADES}
              className="text-xs font-medium text-brand-700 hover:underline"
            >
              Lista de modalidades da renovacao
            </Link>
          </li>
        </ul>
      </section>
      {vendoHistorico ? (
        <HistoricoDoParametro
          chave={['renewal-history', 'todos']}
          carregar={(signal) => renewalSettings.history(undefined, signal)}
          onClose={() => setVendoHistorico(false)}
        />
      ) : null}
    </div>
  );
}

// --- /parametrizacao/renovacao/configuracao -----------------------------------------

/**
 * "Perfis de Usuario para Email" (PAR-179): os perfis da corretora, cada um com a
 * caixa que inclui ou retira o perfil do rascunho.
 */
function PerfisAvisados({
  podeEditar,
  onMudou,
}: {
  podeEditar: boolean;
  onMudou: () => void;
}) {
  const toast = useToast();
  const consulta = useQuery({
    queryKey: ['renewal-notified-roles'],
    queryFn: ({ signal }) => renewalSettings.notifiedRoles(signal),
  });
  const alternar = useMutation({
    mutationFn: (perfil: RenewalNotifiedRole) =>
      perfil.notified
        ? renewalSettings.removeNotifiedRole(perfil.role_id)
        : renewalSettings.addNotifiedRole(perfil.role_id),
    onSuccess: (versao, perfil) => {
      toast.success(
        `${perfil.name} ${perfil.notified ? 'retirado do' : 'incluido no'} rascunho ${versao.version}.`,
      );
      onMudou();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <Card>
      <CardHeader
        title="Perfis de Usuario para Email"
        description="Os perfis da corretora que recebem o aviso de renovacao. A mudanca vai para o rascunho."
      />
      <CardBody>
        {consulta.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os perfis">
            {consulta.error.message}
          </Alert>
        ) : consulta.data.length === 0 ? (
          <p className="text-xs text-ink-500">
            Nenhum perfil da corretora cadastrado. Os perfis ficam em Parametrizacao,
            Perfil da Corretora.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" aria-label="Perfis de usuario para email">
            {consulta.data.map((perfil) => (
              <li key={perfil.role_id} className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-ink-800">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-brand-500)]"
                    checked={perfil.notified}
                    // O perfil que deixou de ser da corretora so pode sair da lista: o
                    // servidor recusa inclui-lo de novo (achado B10 da segunda revisao).
                    disabled={
                      !podeEditar ||
                      alternar.isPending ||
                      (!perfil.broker_scoped && !perfil.notified)
                    }
                    onChange={() => alternar.mutate(perfil)}
                  />
                  {perfil.name}
                </label>
                {perfil.in_active ? <Badge tone="success">Em vigor</Badge> : null}
                {perfil.in_draft ? <Badge tone="info">No rascunho</Badge> : null}
                {!perfil.broker_scoped ? (
                  <Badge tone="warning">Nao e mais perfil da corretora</Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function textoDoNumero(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor);
}

function FormularioDaRenovacao({ dados }: { dados: RenewalSettings }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can(PERM_EDITAR);
  const podeAtivar = can(PERM_ATIVAR);
  const base = dados.draft_setting ?? dados.active_setting;
  const mensagemRef = useRef<RichTextEditorHandle>(null);

  const [titulo, setTitulo] = useState(base?.values.notice_email_subject ?? '');
  const [mensagem, setMensagem] = useState(base?.values.notice_email_message ?? '');
  const [boleto, setBoleto] = useState(textoDoNumero(base?.values.boleto_due_days));
  const [observacao, setObservacao] = useState(dados.draft_setting?.notes ?? '');
  const [ativando, setAtivando] = useState(false);

  function atualizar() {
    void queryClient.invalidateQueries({ queryKey: ['renewal-settings'] });
    void queryClient.invalidateQueries({ queryKey: ['renewal-versions'] });
    void queryClient.invalidateQueries({ queryKey: ['renewal-history'] });
    void queryClient.invalidateQueries({ queryKey: ['renewal-notified-roles'] });
  }

  const salvar = useMutation({
    mutationFn: () =>
      renewalSettings.saveDraft({
        notice_email_subject: titulo,
        notice_email_message: mensagem,
        boleto_due_days: diasDoCampo(boleto),
        notes: observacao,
      }),
    onSuccess: (versao) => {
      toast.success(`Rascunho ${versao.version} salvo.`);
      atualizar();
    },
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const erroDe = (campo: string) => fieldErrorOf(apiError, campo);
  const desligado = !podeEditar;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <div className="flex flex-col gap-5">
      <SituacaoDaVersao
        ativa={dados.active_setting}
        rascunho={dados.draft_setting}
        podeAtivar={podeAtivar}
        onAtivar={() => setAtivando(true)}
      />
      <Alert tone="info" title="Aviso">
        O e-mail de aviso de renovacao automatica vai aos perfis da corretora marcados
        abaixo. O envio fica desligado ate a homologacao da renovacao (REN-002) e do
        provedor de e-mail (EMI-002): o texto e os perfis ficam guardados com versao.
      </Alert>
      <PerfisAvisados podeEditar={podeEditar} onMudou={atualizar} />
      <form className="flex flex-col gap-5" onSubmit={enviar} noValidate>
        {salvar.error &&
        !['notice_email_subject', 'notice_email_message', 'boleto_due_days'].some(
          (campo) => erroDe(campo),
        ) ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <Card>
          <CardHeader
            title="Email de Aviso de Renovacao Automatica"
            description="Titulo e mensagem com as TAGs do e-mail."
          />
          <CardBody className="flex flex-col gap-4">
            <Field
              label="Titulo do email de aviso"
              required
              placeholder="Digite o titulo"
              maxLength={base?.values.notice_email_subject_max_characters ?? 200}
              value={titulo}
              disabled={desligado}
              error={erroDe('notice_email_subject')}
              onChange={(evento) => setTitulo(evento.target.value)}
            />
            <InserirTag
              contexto="email"
              rotulo="+ Inserir Tag no titulo"
              disabled={desligado}
              onIncluir={(marcacao) => setTitulo((atual) => `${atual}${marcacao}`)}
            />
            <InserirTag
              contexto="email"
              disabled={desligado}
              onIncluir={(marcacao) => mensagemRef.current?.inserirTexto(marcacao)}
            />
            <RichTextEditor
              label="Mensagem do email de aviso"
              required
              value={mensagem}
              onChange={setMensagem}
              maxLength={base?.values.notice_email_message_max_characters ?? 20000}
              disabled={desligado}
              error={erroDe('notice_email_message')}
              editorRef={mensagemRef}
              rows={8}
            />
            <Field
              label="Vencimento do boleto da renovacao (dias)"
              type="number"
              inputMode="numeric"
              min={0}
              max={36500}
              hint="Dias a partir do inicio de vigencia da renovada (a confirmar)."
              value={boleto}
              disabled={desligado}
              error={erroDe('boleto_due_days')}
              onChange={(evento) => setBoleto(evento.target.value)}
            />
          </CardBody>
        </Card>
        {podeEditar ? (
          <Textarea
            label="Observacao da versao"
            maxLength={4000}
            value={observacao}
            onChange={(evento) => setObservacao(evento.target.value)}
          />
        ) : null}
        <div className="flex flex-wrap justify-between gap-2">
          <Voltar para={BASE} />
          {podeEditar ? (
            <Button type="submit" loading={salvar.isPending}>
              Salvar
            </Button>
          ) : null}
        </div>
      </form>
      {ativando && dados.draft_setting ? (
        <AtivarVersao
          versao={dados.draft_setting.version}
          onFechar={() => setAtivando(false)}
          ativar={(justificativa) => renewalSettings.activate(justificativa)}
          onAtivado={() => {
            setAtivando(false);
            toast.success('Versao ativada.');
            atualizar();
          }}
        />
      ) : null}
    </div>
  );
}

function resumoDaRenovacao(versao: RenewalVersion): string {
  const perfis = versao.values.notified_roles.map((perfil) => perfil.name);
  return [
    versao.values.notice_email_subject
      ? `titulo "${versao.values.notice_email_subject}"`
      : 'sem titulo',
    perfis.length > 0 ? `perfis: ${perfis.join(', ')}` : 'sem perfis avisados',
  ].join('; ');
}

/**
 * Configuracao da renovacao automatica (`configurar-renovacao` do OnPoint, PAR-177 a
 * PAR-183): Aviso, Perfis de Usuario para Email, Email de Aviso de Renovacao Automatica
 * com Titulo e Mensagem, "+ Inserir Tag", Historico e Voltar.
 */
export function RenewalSettingsPage() {
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const consulta = useQuery({
    queryKey: ['renewal-settings'],
    queryFn: ({ signal }) => renewalSettings.get(signal),
    enabled: podeLer,
  });
  const versoes = useQuery({
    queryKey: ['renewal-versions'],
    queryFn: ({ signal }) => renewalSettings.versions(signal),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha itens={trilhaDaRenovacao} atual="Renovacao Automatica de Apolices" />
      <PageHeader
        title="Renovacao Automatica de Apolices"
        description="Perfis avisados e o e-mail de aviso da renovacao automatica."
        actions={
          podeLer ? (
            <>
              <Link
                to={MODALIDADES}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Lista de modalidades da renovacao
              </Link>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVendoHistorico(true)}
              >
                Historico de alteracoes
              </Button>
            </>
          ) : undefined
        }
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      {consulta.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {consulta.error.message}
        </Alert>
      ) : (
        // A chave muda so com a versao em vigor. Marcar um perfil abre ou atualiza o
        // rascunho, e a chave com o rascunho remontava o formulario e descartava o texto
        // digitado e nao salvo (achado B9 da segunda revisao). Salvar e marcar perfis nao
        // mudam o texto no servidor, entao o formulario continua com o que esta na tela.
        <FormularioDaRenovacao
          key={consulta.data.active_setting?.id ?? 'sem-versao-em-vigor'}
          dados={consulta.data}
        />
      )}
      <VersoesDoParametro
        versoes={versoes.data ?? []}
        carregando={versoes.isPending}
        erro={versoes.error}
        resumo={resumoDaRenovacao}
      />
      {vendoHistorico ? (
        <HistoricoDoParametro
          chave={['renewal-history', 'configuracao']}
          carregar={(signal) => renewalSettings.history('configuracao', signal)}
          onClose={() => setVendoHistorico(false)}
        />
      ) : null}
    </div>
  );
}

// --- Modalidades da renovacao -------------------------------------------------------

function valoresDe(item: RenewalModality): RenewalModalityValues | null {
  return (item.draft_setting ?? item.active_setting)?.values ?? null;
}

/**
 * Lista das modalidades da renovacao (`modalidades-renovacao`, PAR-184 e PAR-185): os
 * prazos, o indice e a situacao de cada modalidade, "Nova modalidade" e o historico.
 */
export function RenewalModalitiesPage() {
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const podeEditar = can(PERM_EDITAR);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const indices = useIndices();
  const consulta = useQuery({
    queryKey: ['renewal-modalities'],
    queryFn: ({ signal }) => renewalSettings.listModalities(signal),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha itens={trilhaDaRenovacao} atual="Lista modalidades renovacao" />
      <PageHeader
        title="Modalidades da renovacao"
        description="Prazos da renovacao por modalidade. O prazo para criacao da cotacao em vigor e a janela da fila de Renovacoes."
        actions={
          podeLer ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVendoHistorico(true)}
              >
                Historico de alteracoes
              </Button>
              {podeEditar ? (
                <Link
                  to={`${MODALIDADES}/nova`}
                  className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-xs font-semibold text-white hover:bg-brand-700"
                >
                  Nova modalidade
                </Link>
              ) : null}
            </>
          ) : undefined
        }
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as modalidades">
          {consulta.error.message}
        </Alert>
      ) : null}
      <Card>
        <DataTable
          columns={[
            {
              key: 'modalidade',
              header: 'Modalidade',
              render: (item: RenewalModality) => (
                <div className="min-w-0">
                  <p className="font-medium text-ink-900">
                    {item.modality.name || 'Modalidade sem cadastro'}
                  </p>
                  <p className="truncate text-2xs text-ink-500">
                    {item.modality.product_name}
                  </p>
                </div>
              ),
            },
            {
              key: 'vigencia',
              header: 'Prazo maximo de vigencia',
              hideBelow: 'lg',
              render: (item: RenewalModality) => {
                const dias = valoresDe(item)?.max_term_days ?? null;
                return dias === null ? 'Mantem o original' : textoDosDias(dias);
              },
            },
            {
              key: 'cotacao',
              header: 'Criacao da cotacao',
              render: (item: RenewalModality) =>
                textoDosDias(valoresDe(item)?.quote_lead_days),
            },
            {
              key: 'cancelamento',
              header: 'Cancelamento apos o fim',
              hideBelow: 'lg',
              render: (item: RenewalModality) =>
                textoDosDias(valoresDe(item)?.auto_cancel_after_days),
            },
            {
              key: 'emissao',
              header: 'Emissao',
              hideBelow: 'md',
              render: (item: RenewalModality) =>
                textoDosDias(valoresDe(item)?.issuance_deadline_days),
            },
            {
              key: 'indice',
              header: 'Indice',
              hideBelow: 'md',
              render: (item: RenewalModality) =>
                nomeDoIndice(indices.data, valoresDe(item)?.economic_index ?? null),
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (item: RenewalModality) => (
                <SituacaoDaModalidade
                  ativa={item.active_setting}
                  rascunho={item.draft_setting}
                />
              ),
            },
            {
              key: 'acoes',
              header: 'Acoes',
              align: 'right',
              render: (item: RenewalModality) => (
                <LinkComIcone
                  acao="editar"
                  rotulo="Editar"
                  to={`${MODALIDADES}/${item.setting_id}`}
                />
              ),
            },
          ]}
          items={consulta.data ?? []}
          keyOf={(item) => item.modality.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="layers"
              title="Nenhuma modalidade com parametro de renovacao"
              description="Use Nova modalidade para guardar os prazos da renovacao de uma modalidade."
            />
          }
        />
      </Card>
      <div>
        <Voltar para={BASE} />
      </div>
      {vendoHistorico ? (
        <HistoricoDoParametro
          chave={['renewal-history', 'modalidades']}
          carregar={(signal) => renewalSettings.history('modalidades', signal)}
          onClose={() => setVendoHistorico(false)}
        />
      ) : null}
    </div>
  );
}

/** Caixa de "Flags", com o motivo quando desligada. */
function Interruptor({
  rotulo,
  marcado,
  onChange,
  disabled,
  explicacao,
}: {
  rotulo: string;
  marcado: boolean;
  onChange: (valor: boolean) => void;
  disabled: boolean;
  explicacao: string;
}) {
  const idDoCampo = useId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          id={idDoCampo}
          type="checkbox"
          className="size-4 accent-[var(--color-brand-500)]"
          checked={marcado}
          disabled={disabled}
          onChange={(evento) => onChange(evento.target.checked)}
        />
        <FieldLabel
          htmlFor={idDoCampo}
          label={rotulo}
          className="text-sm text-ink-800"
        />
      </div>
      <p className="pl-6 text-2xs text-ink-500">{explicacao}</p>
    </div>
  );
}

/**
 * Cadastro e edicao da modalidade da renovacao (`criar-modalidade-renovacao` e
 * `editar-modalidade-renovacao/:token`, PAR-186 a PAR-200): Dados da Modalidade,
 * Importante, os prazos em dias, as Flags e o indice.
 */
export function RenewalModalityFormPage() {
  const { settingId } = useParams();
  const novo = settingId === undefined;
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const indices = useIndices();

  const detalhe = useQuery({
    queryKey: ['renewal-modality', settingId],
    queryFn: ({ signal }) => renewalSettings.getModality(settingId ?? '', signal),
    enabled: podeLer && !novo,
  });
  const cadastradas = useQuery({
    queryKey: ['renewal-modalities'],
    queryFn: ({ signal }) => renewalSettings.listModalities(signal),
    enabled: podeLer && novo,
  });
  const modalidades = useQuery({
    queryKey: ['modalities', 'ativas'],
    queryFn: ({ signal }) =>
      parametrization.listModalities({ status: 'ativo', limit: 200 }, signal),
    enabled: podeLer && novo && can('parametrizacao.modalidade.view'),
  });

  const cabecalho = (
    <>
      <Trilha
        itens={[
          ...trilhaDaRenovacao,
          { rotulo: 'Lista modalidades renovacao', para: MODALIDADES },
        ]}
        atual={novo ? 'Nova modalidade' : 'Editar modalidade'}
      />
      <PageHeader
        title={novo ? 'Nova modalidade da renovacao' : 'Modalidade da renovacao'}
        description="Dados da Modalidade: prazos da renovacao, flags e indice de reajuste."
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;
  if (!novo && detalhe.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!novo && detalhe.isError) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Alert tone="danger" title="Nao foi possivel carregar a modalidade">
          {detalhe.error.message}
        </Alert>
      </div>
    );
  }

  const atual = detalhe.data?.draft_setting ?? detalhe.data?.active_setting ?? null;
  const jaCadastradas = new Set(
    (cadastradas.data ?? []).map((item) => item.modality.id),
  );
  const opcoesDeModalidade = (modalidades.data?.items ?? [])
    .filter((modalidade) => !jaCadastradas.has(modalidade.id))
    .map((modalidade) => ({ value: modalidade.id, label: modalidade.name }));

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      <FormularioDaModalidadeDeRenovacao
        key={`${atual?.id ?? 'nova'}-${atual?.updated_at ?? ''}`}
        novo={novo}
        detalhe={detalhe.data}
        opcoesDeModalidade={opcoesDeModalidade}
        indices={(indices.data ?? [])
          .filter((indice) => indice.active)
          .map((indice) => ({ value: indice.code, label: indice.name }))}
        onSalvo={(id) => {
          void queryClient.invalidateQueries({ queryKey: ['renewal-modalities'] });
          void queryClient.invalidateQueries({ queryKey: ['renewal-modality'] });
          void queryClient.invalidateQueries({ queryKey: ['renewal-history'] });
          toast.success('Rascunho salvo.');
          void navigate(`${MODALIDADES}/${id}`, { replace: !novo });
        }}
      />
    </div>
  );
}

function FormularioDaModalidadeDeRenovacao({
  novo,
  detalhe,
  opcoesDeModalidade,
  indices,
  onSalvo,
}: {
  novo: boolean;
  detalhe: RenewalModality | undefined;
  opcoesDeModalidade: { value: string; label: string }[];
  indices: { value: string; label: string }[];
  onSalvo: (settingId: string) => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can(PERM_EDITAR);
  const podeAtivar = can(PERM_ATIVAR);
  const valores = (detalhe?.draft_setting ?? detalhe?.active_setting)?.values;

  const [modalidade, setModalidade] = useState('');
  const [vigencia, setVigencia] = useState(textoDoNumero(valores?.max_term_days));
  const [cotacao, setCotacao] = useState(textoDoNumero(valores?.quote_lead_days));
  const [cancelamento, setCancelamento] = useState(
    textoDoNumero(valores?.auto_cancel_after_days),
  );
  const [emissao, setEmissao] = useState(
    textoDoNumero(valores?.issuance_deadline_days),
  );
  const [emissaoAutomatica, setEmissaoAutomatica] = useState(
    valores?.auto_issue_enabled ?? false,
  );
  const [dispensaDoLimite, setDispensaDoLimite] = useState(
    valores?.bypass_available_limit ?? false,
  );
  const [indice, setIndice] = useState(valores?.economic_index ?? '');
  const [observacao, setObservacao] = useState(detalhe?.draft_setting?.notes ?? '');
  const [ativando, setAtivando] = useState(false);

  const corpo = () => ({
    max_term_days: diasDoCampo(vigencia),
    quote_lead_days: diasDoCampo(cotacao),
    auto_cancel_after_days: diasDoCampo(cancelamento),
    issuance_deadline_days: diasDoCampo(emissao),
    auto_issue_enabled: emissaoAutomatica,
    bypass_available_limit: dispensaDoLimite,
    economic_index: indice === '' ? null : indice,
    notes: observacao,
  });
  const salvar = useMutation({
    mutationFn: () =>
      novo
        ? renewalSettings.createModality({ modality_id: modalidade, ...corpo() })
        : renewalSettings.saveModality(detalhe?.setting_id ?? '', corpo()),
    onSuccess: (versao) => onSalvo(versao.id),
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const erroDe = (campo: string) => fieldErrorOf(apiError, campo);
  const campos = [
    'modality_id',
    'max_term_days',
    'quote_lead_days',
    'auto_cancel_after_days',
    'issuance_deadline_days',
    'economic_index',
  ];
  const desligado = !podeEditar;

  return (
    <div className="flex flex-col gap-5">
      {!novo && detalhe ? (
        <SituacaoDaVersao
          ativa={detalhe.active_setting}
          rascunho={detalhe.draft_setting}
          podeAtivar={podeAtivar}
          onAtivar={() => setAtivando(true)}
        />
      ) : null}
      <Alert tone="warning" title="Importante">
        Os prazos e as flags ficam guardados com versao e nao disparam rotina: nenhuma
        cotacao de renovacao e criada, nenhuma apolice e emitida ou cancelada
        automaticamente ate a homologacao (REN-001). O prazo para criacao da cotacao da
        versao em vigor e a janela desta modalidade na fila de Renovacoes.
      </Alert>
      <Card>
        <CardHeader title="Dados da Modalidade" />
        <CardBody>
          <form
            className="flex flex-col gap-5"
            noValidate
            onSubmit={(evento) => {
              evento.preventDefault();
              salvar.mutate();
            }}
          >
            {salvar.error && !campos.some((campo) => erroDe(campo)) ? (
              <Alert tone="danger" title="Nao foi possivel salvar">
                {salvar.error.message}
              </Alert>
            ) : null}
            {novo ? (
              <Select
                label="Modalidade"
                required
                placeholder="Escolha a modalidade"
                options={opcoesDeModalidade}
                value={modalidade}
                disabled={desligado}
                error={erroDe('modality_id')}
                onChange={(evento) => setModalidade(evento.target.value)}
              />
            ) : (
              <Field
                label="Modalidade"
                value={detalhe?.modality.name ?? ''}
                disabled
                readOnly
              />
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Prazo maximo de vigencia (dias)"
                type="number"
                inputMode="numeric"
                min={1}
                max={36500}
                placeholder="Digite o prazo maximo de vigencia"
                hint="Em branco mantem o prazo da apolice original."
                value={vigencia}
                disabled={desligado}
                error={erroDe('max_term_days')}
                onChange={(evento) => setVigencia(evento.target.value)}
              />
              <Field
                label="Prazo para criacao da cotacao (dias)"
                type="number"
                inputMode="numeric"
                min={0}
                max={36500}
                placeholder="Digite o prazo para criacao da cotacao"
                hint="Dias antes do fim da vigencia. Na versao em vigor, e a janela da fila de Renovacoes."
                value={cotacao}
                disabled={desligado}
                error={erroDe('quote_lead_days')}
                onChange={(evento) => setCotacao(evento.target.value)}
              />
              <Field
                label="Prazo (em dias) para cancelamento automatico da apolice, apos o fim da vigencia"
                type="number"
                inputMode="numeric"
                min={0}
                max={36500}
                placeholder="Digite o prazo para cancelamento da apolice"
                value={cancelamento}
                disabled={desligado}
                error={erroDe('auto_cancel_after_days')}
                onChange={(evento) => setCancelamento(evento.target.value)}
              />
              <Field
                label="Prazo para emissao da apolice (dias)"
                type="number"
                inputMode="numeric"
                min={0}
                max={36500}
                placeholder="Digite o prazo para emissao da apolice"
                value={emissao}
                disabled={desligado}
                error={erroDe('issuance_deadline_days')}
                onChange={(evento) => setEmissao(evento.target.value)}
              />
            </div>
            <fieldset className="flex flex-col gap-3 rounded-md border border-line p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink-500">
                Flags
              </legend>
              <Interruptor
                rotulo="Cancelamento automatico da apolice original"
                marcado={false}
                onChange={() => undefined}
                disabled
                explicacao="Bloqueado: o cancelamento automatico depois do fim da vigencia aguarda a decisao juridica (PJ-01). O prazo acima pode ser guardado."
              />
              <Interruptor
                rotulo="Emissao automatica da renovada"
                marcado={emissaoAutomatica}
                onChange={setEmissaoAutomatica}
                disabled={desligado}
                explicacao="Guardado sem efeito: nenhuma apolice e emitida automaticamente."
              />
              <Interruptor
                rotulo="Dispensa do limite disponivel do tomador"
                marcado={dispensaDoLimite}
                onChange={setDispensaDoLimite}
                disabled={desligado}
                explicacao="Guardado sem efeito: o limite do tomador continua sendo conferido como hoje (LIM-010)."
              />
            </fieldset>
            <Select
              label="Indice de reajuste da importancia segurada"
              placeholder="Nenhum"
              options={indices}
              value={indice}
              disabled={desligado}
              error={erroDe('economic_index')}
              hint="Guardado sem formula: o reajuste e regra pendente (F-14, F-18)."
              onChange={(evento) => setIndice(evento.target.value)}
            />
            {podeEditar ? (
              <Textarea
                label="Observacao da versao"
                maxLength={4000}
                value={observacao}
                onChange={(evento) => setObservacao(evento.target.value)}
              />
            ) : null}
            <div className="flex flex-wrap justify-between gap-2">
              <Voltar para={MODALIDADES} />
              {podeEditar ? (
                <Button
                  type="submit"
                  loading={salvar.isPending}
                  disabled={novo && modalidade === ''}
                >
                  Salvar
                </Button>
              ) : null}
            </div>
          </form>
        </CardBody>
      </Card>
      {detalhe ? (
        <VersoesDoParametro
          versoes={detalhe.versions ?? []}
          carregando={false}
          erro={null}
          resumo={(versao) =>
            `cotacao ${textoDosDias(versao.values.quote_lead_days)}; emissao ${textoDosDias(versao.values.issuance_deadline_days)}`
          }
        />
      ) : null}
      {ativando && detalhe?.draft_setting ? (
        <AtivarVersao
          versao={detalhe.draft_setting.version}
          onFechar={() => setAtivando(false)}
          ativar={(justificativa) =>
            renewalSettings.activateModality(
              detalhe.draft_setting?.id ?? '',
              justificativa,
            )
          }
          onAtivado={() => {
            setAtivando(false);
            toast.success('Versao ativada.');
            void queryClient.invalidateQueries({ queryKey: ['renewal-modality'] });
            void queryClient.invalidateQueries({ queryKey: ['renewal-modalities'] });
            void queryClient.invalidateQueries({ queryKey: ['renewal-history'] });
          }}
        />
      ) : null}
    </div>
  );
}
